import React, { useContext } from "react";
import { Component } from "../../interfaces/Form/Component";
import { FormComponent } from "../../interfaces/Form/FormComponent";
import { LayoutComponent } from "../../interfaces/Form/LayoutComponent";
import { VariableComponent } from "../../interfaces/Form/VariableComponent";
import { isLayoutComponent, isWidget } from "../../utils/components";
import { FORM_HEAD_ID, FORM_SUBMIT_ID } from "../../constants";
import FormContext from "../../context/FormContext";

interface Props {
  component: Component;
  id: string;
}

const FormComponentRenderer = ({ component, id }: Props) => {
  const context = useContext(FormContext);

  const renderVariable = (component: VariableComponent) => {
    const { props, Render } = component;
    const { repeat, ...restProps } = props;
    return (
      <Render
        {...restProps}
        max={repeat && repeat.max}
        min={repeat && repeat.min}
      />
    );
  };

  const renderLayoutComponent = (component: LayoutComponent) => {
    const { props, Render } = component;
    return (
      <Render key={props.id} {...props}>
        {props.components &&
          props.components.map((id: string) =>
            renderComponent(id, context.components[id])
          )}
      </Render>
    );
  };

  const renderFormComponent = (component: FormComponent) => {
    const { props, Render } = component;
    return <Render key={props.id} {...props} />;
  };

  const renderCustomForm = (id: string, component: FormComponent) => {
    const { props, Render } = component;
    // @ts-ignore
    return <Render id={id} key={id} {...props} />;
  };

  const renderComponent = (id: string, component: Component) => {
    if (!component) {
      console.error(`Found non-existing component "${id}".`);
      return null;
    }
    if (component.props.id === FORM_HEAD_ID) {
      return context.formHeadRender(component);
    }
    if (component.props.id === FORM_SUBMIT_ID) {
      return context.formSubmitRender(component);
    }
    if (component.props.customForm) {
      return renderCustomForm(id, component as FormComponent);
    }
    if (isWidget(component, "Variable")) {
      return renderVariable(component as VariableComponent);
    }
    if (isLayoutComponent(component)) {
      return renderLayoutComponent(component as LayoutComponent);
    }
    return renderFormComponent(component as FormComponent);
  };

  return renderComponent(id, component);
};

export default FormComponentRenderer;
